import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import { isOwnerHost } from "./utils/portalHost";
import { initSessionRefresh } from "./utils/sessionRefresh";
import { initInstallCapture } from "./components/pwa/installPrompt";
import { Box } from "@chakra-ui/react";
import GuestErrorBoundary from "./components/guestbook/GuestErrorBoundary";
import { useState, useEffect, useRef, Suspense, lazy } from "react";
import { GUEST_LANGUAGE_CODES, DASHBOARD_LANGUAGE_CODES } from "./constants/guestLanguages";
import i18n from "./i18n";
import DelayedSpinner from "./ui/feedback/Spinner";
import ErrorPage from "./ui/feedback/ErrorPage";
import ErrorPageWrapper from "./components/ErrorPageWrapper";
import DomainGuard from "./components/DomainGuard";
import PaymentConfirmation from "./pages/checkin/steps/PaymentConfirmation";
import DashboardPageContainer from "./components/DashboardPageContainer";
import { HotelProvider } from "./contexts/HotelContext";
import { OwnerAuthProvider } from "./hooks/auth/useOwnerAuth";
import { OwnerUnitProvider } from "./contexts/OwnerUnitContext";
import { useEntitlements } from "./hooks/useEntitlements";
import { moduleForPath } from "./constants/productConfig";

// Imports dynamiques des composants de navigation
const WebsiteNavbar = lazy(() => import("./nav/WebsiteNavbar"));
const BookletNavbar = lazy(() => import("./nav/BookletNavbar"));
const DashboardNavbar = lazy(() => import("./nav/DashboardNavbar"));
const DashboardTopBar = lazy(() => import("./nav/DashboardTopBar"));
const CommandPalette = lazy(() => import("./components/search/CommandPalette"));
const GuestyImportProgress = lazy(() => import("./components/dashboard/GuestyImportProgress"));
const LunaSetupAssistant = lazy(() => import("./components/tour/LunaSetupAssistant"));

// Imports dynamiques des pages du site
const Home = lazy(() => import("./pages/website/Home"));
const About = lazy(() => import("./pages/website/About"));
const Pricing = lazy(() => import("./pages/website/Pricing"));
const Contact = lazy(() => import("./pages/website/Contact"));
const CookiePolicy = lazy(() => import("./pages/website/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("./pages/website/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/website/TermsOfService"));
const ThreeStarHotel = lazy(() =>
  import("./pages/website/pricing/ThreeStarHotel")
);
const FiveStarHotel = lazy(() =>
  import("./pages/website/pricing/FiveStarHotel")
);
const AirbnbAgency = lazy(() => import("./pages/website/pricing/AirbnbAgency"));

// Imports dynamiques des pages produit
const Guestbook = lazy(() =>
  import("./pages/website/solutions/Guestbook")
);
const Upsells = lazy(() => import("./pages/website/solutions/Upsells"));
const OnlineCheckin = lazy(() =>
  import("./pages/website/solutions/OnlineCheckin")
);
const ChatbotProduct = lazy(() =>
  import("./pages/website/solutions/ChatbotProduct")
);
const KycProduct = lazy(() => import("./pages/website/solutions/KycProduct"));
const IgloohomeIntegration = lazy(() =>
  import("./pages/website/integrations/IgloohomeIntegration")
);

// Imports dynamiques des pages d'authentification
const Login = lazy(() => import("./pages/auth/Login"));
const OAuthAuthorize = lazy(() => import("./pages/auth/OAuthAuthorize"));
const OnboardingFlow = lazy(() => import("./pages/auth/OnboardingFlow"));
const CreateHotelSetup = lazy(() => import("./pages/auth/CreateHotelSetup"));
const OAuthSuccess = lazy(() => import("./pages/auth/OAuthSuccess"));
const SetPassword = lazy(() => import("./pages/auth/SetPassword"));

// Imports dynamiques des pages du dashboard
const DashboardHome = lazy(() => import("./pages/dashboard/DashboardHome"));
const Calendar = lazy(() => import("./pages/dashboard/Calendar"));
const Reservations = lazy(() => import("./pages/dashboard/Reservations"));
const GuestData = lazy(() => import("./pages/dashboard/GuestData"));
const Preview = lazy(() => import("./pages/dashboard/Preview"));
const Payments = lazy(() => import("./pages/dashboard/Payments"));
const LunaSection = lazy(() => import("./pages/dashboard/LunaSection"));
const ReservationDetails = lazy(() =>
  import("./components/calendar/ReservationDetails")
);
const CheckInForm = lazy(() => import("./pages/checkin/CheckInForm"));
const EarlyCheckinPurchase = lazy(() => import("./pages/checkin/EarlyCheckinPurchase"));
const LateCheckoutPurchase = lazy(() => import("./pages/checkin/LateCheckoutPurchase"));
const ExtendStayPurchase = lazy(() => import("./pages/checkin/ExtendStayPurchase"));
const GuestReview = lazy(() => import("./pages/checkin/GuestReview"));
const ManageUnits = lazy(() => import("./pages/dashboard/ManageUnits"));
const UnitDetails = lazy(() => import("./components/manageUnits/UnitDetails"));
const StaffMobileInterface = lazy(() => import("./components/taskManager/StaffMobileInterface"));
const LeadsManagement = lazy(() => import("./pages/dashboard/LeadsManagement"));

// Guest Experience
const ChatManager = lazy(() => import("./pages/dashboard/Guest Experience/ChatManager"));
const GuestAppStudio = lazy(() => import("./pages/dashboard/Guest Experience/GuestAppStudio"));
const GuestAppPreviewFrame = lazy(() => import("./pages/GuestAppPreviewFrame"));
const CheckinPreviewFrame = lazy(() => import("./pages/CheckinPreviewFrame"));
const CheckinStudio = lazy(() => import("./pages/dashboard/Guest Experience/CheckinStudio"));
const UpsellsHub = lazy(() => import("./pages/dashboard/Guest Experience/UpsellsHub"));

// Operations
const OperationsCenter = lazy(() => import("./pages/dashboard/Operations/OperationsCenter"));
const CleanerCalendarPage = lazy(() => import("./pages/dashboard/Operations/CleanerCalendarPage"));
const ChecklistsPage = lazy(() => import("./pages/dashboard/Operations/ChecklistsPage"));

// Financials
const OwnerPayouts = lazy(() => import("./pages/dashboard/Financials/OwnerPayouts"));
const Financials = lazy(() => import("./pages/dashboard/Financials/Financials"));
const Expenses = lazy(() => import("./pages/dashboard/Financials/Expenses"));
// Legal invoices (facture / avoir). The page was written but never imported,
// so the whole module — credit notes included — was unreachable.
const Invoices = lazy(() => import("./pages/dashboard/Financials/Invoices"));
const Owners = lazy(() => import("./pages/dashboard/Financials/Owners"));
const FeeManagement = lazy(() => import("./pages/dashboard/Financials/FeeManagement"));
const Settings = lazy(() => import("./pages/dashboard/Settings"));
const Notifications = lazy(() => import("./pages/dashboard/Notifications"));
const Updates = lazy(() => import("./pages/dashboard/Updates"));
const IglooconnectCallback = lazy(() => import("./pages/integrations/IglooconnectCallback"));
const StripeConnectCallback = lazy(() => import("./pages/integrations/StripeConnectCallback"));
const Onboarding = lazy(() => import("./pages/onboarding/Onboarding"));

// Imports dynamiques des pages du Owner Portal
const OwnerLogin = lazy(() => import("./pages/ownerPortal/OwnerLogin"));
const OwnerSetPassword = lazy(() => import("./pages/ownerPortal/OwnerSetPassword"));
const OwnerForgotPassword = lazy(() => import("./pages/ownerPortal/OwnerForgotPassword"));
const OwnerResetPassword = lazy(() => import("./pages/ownerPortal/OwnerResetPassword"));
const OwnerLayout = lazy(() => import("./pages/ownerPortal/OwnerLayout"));
const OwnerAccueil = lazy(() => import("./pages/ownerPortal/OwnerAccueil"));
const OwnerReleves = lazy(() => import("./pages/ownerPortal/OwnerReleves"));
const OwnerSettings = lazy(() => import("./pages/ownerPortal/OwnerSettings"));
const OwnerActivity = lazy(() => import("./pages/ownerPortal/OwnerActivity"));

// Imports dynamiques des pages de paiement
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const StripePaymentForm = lazy(() => import("./components/payments/StripePaymentForm"));
const PaymentRedirect = lazy(() => import("./components/payments/PaymentRedirect"));

// Imports dynamiques des autres composants
const Information = lazy(() => import("./pages/Information"));
const Taxi = lazy(() => import("./pages/Taxi"));
const PaymentSuccess = lazy(() => import("./components/payments/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./components/payments/PaymentCancel"));
const PaymentPending = lazy(() => import("./components/payments/PaymentPending"));
const PreviewChatbot = lazy(() => import("./components/preview/PreviewChatbot"));
const UnitWelcomeBook = lazy(() => import("./components/guestbook/UnitWelcomeBook"));

const Services = lazy(() => import("./components/editwelcomeBook/Services"));
const GuestbookRedirect = lazy(() => import("./pages/GuestbookRedirect"));
const ServiceDetails = lazy(() => import("./components/guestbook/ServiceDetails"));

// Ancienne ErrorPage inline supprimée au profit d'un composant générique utils/ErrorPage

const ProtectedDashboardRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");
  const location = useLocation();

  if (!token || !user) {
    console.log("No token or user found, redirecting to login");
    return <Navigate to="/login" />;
  }

  // Ne pas rediriger vers le dashboard pendant le flux d'onboarding
  const isOnboardingFlow = location.pathname === "/onboarding";

  // Si l'utilisateur a une companyId, il peut accéder au dashboard
  if (user.companyId && !isOnboardingFlow) return children;

  // Sinon, rediriger vers l'onboarding
  if (!isOnboardingFlow) {
    console.log("User has no companyId, redirecting to onboarding");
    return <Navigate to="/onboarding" />;
  }

  return children;
};

// Route guard (defense-in-depth layer 2 of 3): on the dashboard, if the current
// path belongs to a product module the company does NOT have, redirect to the
// dashboard home. Mounted ONCE inside the dashboard layout — it reads the path
// itself, so individual routes are never wrapped. Backward-compatible: while
// entitlements load, or when a module is undefined, hasModule() returns true and
// nothing is redirected. Core paths (moduleForPath === null) are always allowed.
const ModuleGuard = () => {
  const location = useLocation();
  const { hasModule } = useEntitlements();

  const moduleName = moduleForPath(location.pathname);
  if (moduleName && !hasModule(moduleName)) {
    return <Navigate to="/dashboard" replace />;
  }
  return null;
};

const LoadingFallback = () => <DelayedSpinner minDelay={50} />;

// Owner host legacy-URL alias: /:slug/owner/home → /:slug/home (old emails,
// push payloads and app-host redirects keep working on the short-path host)
const StripOwnerSegment = () => {
  const { pathname, search, hash } = useLocation();
  // Bare /:slug/owner meant "portal home" on the legacy tree, while /:slug is
  // the login route on this host — send it to home to keep the two consistent
  if (/^\/[^/]+\/owner\/?$/.test(pathname)) {
    const slug = pathname.split('/')[1];
    return <Navigate to={`/${slug}/home${search}${hash}`} replace />;
  }
  const target = pathname.replace(/\/owner(\/|$)/, '/').replace(/\/$/, '') || '/';
  return <Navigate to={`${target}${search}${hash}`} replace />;
};

// Guest pages: any guest language; the dashboard stays in its own languages.
const GUEST_LANGUAGE_KEY = "guestLang";
const toGuestLanguage = (raw) => {
  const code = (raw || "").slice(0, 2).toLowerCase();
  return GUEST_LANGUAGE_CODES.includes(code) ? code : null;
};
// ?lang=fr-FR → "fr" (and remembered for the next visit); else the guest's last choice; else null.
const resolveGuestLanguage = (search) => {
  const fromUrl = toGuestLanguage(new URLSearchParams(search).get("lang"));
  try {
    if (fromUrl) localStorage.setItem(GUEST_LANGUAGE_KEY, fromUrl);
    return fromUrl || toGuestLanguage(localStorage.getItem(GUEST_LANGUAGE_KEY));
  } catch {
    return fromUrl;
  }
};

const AppContent = () => {
  const location = useLocation();
  const [lang, setLang] = useState("en");
  const apartmentId = localStorage.getItem("currentApartmentId");
  const isApartmentIdValid = apartmentId && !isNaN(apartmentId);

  // Détermine si l'URL contient un apartmentId
  const isBookletRoute =
    /\/apartment\/\d+|\/services\/\d+|\/activities\/\d+|\/taxi\/\d+|\/chatbot\/\d+/.test(
      location.pathname
    );
  const isDashboardRoute = location.pathname.startsWith("/dashboard");
  const isPublicHotelRoute =
    location.pathname.startsWith("/:slug") ||
    (!location.pathname.startsWith("/dashboard") &&
      !location.pathname.startsWith("/about") &&
      !location.pathname.startsWith("/pricing") &&
      !location.pathname.startsWith("/contact") &&
      !location.pathname.startsWith("/signup") &&
      !location.pathname.startsWith("/login") &&
      !location.pathname.startsWith("/solutions") &&
      location.pathname !== "/integrations/igloohome" &&
      location.pathname !== "/");

  const isAuthRoute =
    location.pathname === "/signup" || location.pathname === "/login";
  const isCheckInRoute = location.pathname.startsWith("/checkin");
  // On the dedicated owner host, EVERY path is owner-portal (short URLs carry no /owner/ segment)
  const isOwnerRoute = isOwnerHost() || location.pathname.includes("/owner/") || location.pathname.endsWith("/owner");

  // Guest-facing surfaces (check-in, guest app, guest payment) use the guest's language — for
  // now ?lang= (en/fr/es) or the guest's last choice, English otherwise — while the admin dashboard keeps
  // the user's detected language. i18n is a single global
  // Sliding admin session: silently renew the 30d token on boot + every return
  // to foreground (iOS PWA resurrections) — active users never see the login.
  // Also capture Android's beforeinstallprompt EARLY (it fires once, often
  // before lazy chunks load) so InstallAppButton can trigger the native dialog.
  useEffect(() => { initSessionRefresh(); initInstallCapture(); }, []);

  // instance, so we force "en" on guest routes and restore the previous language on exit.
  const dashLngRef = useRef(null);
  useEffect(() => {
    const p = location.pathname;
    const isGuestSurface = /\/(checkin|guestbook)\//.test(p) || p.startsWith("/payment");
    if (isGuestSurface) {
      const guestLng = resolveGuestLanguage(location.search) || "en";
      if (i18n.language !== guestLng) {
        if (dashLngRef.current == null) dashLngRef.current = i18n.language;
        i18n.changeLanguage(guestLng);
      }
    } else if (dashLngRef.current) {
      i18n.changeLanguage(DASHBOARD_LANGUAGE_CODES.includes(dashLngRef.current) ? dashLngRef.current : "en");
      dashLngRef.current = null;
    } else if (!DASHBOARD_LANGUAGE_CODES.includes((i18n.language || "en").slice(0, 2))) {
      // A German phone opening the dashboard: guest-only languages are partial there.
      i18n.changeLanguage("en");
    }
  }, [location.pathname, location.search]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      {!isPublicHotelRoute &&
        !isAuthRoute &&
        !isCheckInRoute &&
        !isOwnerRoute &&
        (isDashboardRoute ? (
          <>
            <DashboardNavbar />
            {/* Fixed desktop top bar (⌘K search, notifications, settings, account)
                and the global ⌘K palette — mounted once so they survive navigation. */}
            <Suspense fallback={null}>
              <DashboardTopBar />
            </Suspense>
            <Suspense fallback={null}>
              <CommandPalette />
            </Suspense>
            {/* Product-entitlements route guard — redirects away from any dashboard
                path whose module is disabled for this company (single mount). */}
            <ModuleGuard />
            {/* Floating top-right indicator for ongoing Guesty background imports */}
            <Suspense fallback={null}>
              <GuestyImportProgress />
            </Suspense>
            {/* Global, navigation-surviving Luna setup assistant (guided cross-page setup) */}
            <Suspense fallback={null}>
              <LunaSetupAssistant />
            </Suspense>
          </>
        ) : isBookletRoute ? (
          <BookletNavbar />
        ) : (
          <WebsiteNavbar />
        ))}
      <Routes>
        {/* Routes publiques (marketing — hostea.io)
            IMPORTANT: any new marketing route must also be added to
            MARKETING_PREFIXES in config/appUrl.js (DomainGuard). */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/pricing/three-star" element={<ThreeStarHotel />} />
        <Route path="/pricing/five-star" element={<FiveStarHotel />} />
        <Route path="/pricing/airbnb" element={<AirbnbAgency />} />

        {/* Routes des pages produit */}
        <Route
          path="/solutions/guestbook"
          element={<Guestbook />}
        />
        <Route path="/solutions/upsells" element={<Upsells />} />
        <Route path="/solutions/online-checkin" element={<OnlineCheckin />} />
        <Route path="/solutions/chatbot" element={<ChatbotProduct />} />
        <Route path="/solutions/kyc" element={<KycProduct />} />

        {/* Partner pages (public marketing) — exact path, distinct from the OAuth callback below */}
        <Route path="/integrations/igloohome" element={<IgloohomeIntegration />} />

        <Route path="/contact" element={<Contact />} />

        {/* Legal pages */}
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        <Route path="/signup" element={<Navigate to="/contact" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/oauth-success" element={<OAuthSuccess />} />
        {/* OAuth consent page for the Hostea MCP server (claude.ai / Claude iOS connectors) */}
        <Route path="/oauth/authorize" element={<Suspense fallback={<div />}><OAuthAuthorize /></Suspense>} />
        <Route path="/integrations/igloohome/callback" element={<Suspense fallback={<div />}><IglooconnectCallback /></Suspense>} />
        <Route path="/integrations/stripe/callback" element={<Suspense fallback={<div />}><StripeConnectCallback /></Suspense>} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/onboarding/:token" element={<Suspense fallback={<div />}><Onboarding /></Suspense>} />
        <Route path="/onboarding" element={<OnboardingFlow />} />
        <Route path="/create-hotel-setup" element={<CreateHotelSetup />} />

        {/* Routes protégées du dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <DashboardHome />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/units"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ManageUnits />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/units/upgrade"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ManageUnits />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/units/new"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <UnitDetails />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/units/:unitId"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <UnitDetails />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/guest-app-studio"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <GuestAppStudio />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/checkin-builder"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <CheckinStudio />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/upsells"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <UpsellsHub />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/calendar"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Calendar />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/reservations"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Reservations />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/calendar/:id"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ReservationDetails />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/payments"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Payments />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/taskmanager"
          element={<Navigate to="/dashboard/settings" replace />}
        />
        <Route
          path="/dashboard/operations"
          element={
            <ProtectedDashboardRoute>
              <OperationsCenter />
            </ProtectedDashboardRoute>
          }
        />
        {/* Canonical home is the Luna hub — deep links (notifications, Operations button) redirect there */}
        <Route
          path="/dashboard/investigations"
          element={<Navigate to="/dashboard/luna" state={{ tab: 'investigations' }} replace />}
        />
        <Route
          path="/dashboard/cleaner-calendar"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <CleanerCalendarPage />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/checklists"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ChecklistsPage />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/owner-payouts"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <OwnerPayouts />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/financials"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Financials />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/expenses"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Expenses />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/invoices"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Invoices />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/owners"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Owners />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/fee-management"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <FeeManagement />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/settings"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Settings />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/updates"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Updates />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/notifications"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Notifications />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/chatbot"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ChatManager />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/data"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <GuestData />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/luna"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <LunaSection />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <ProtectedDashboardRoute>
              <LeadsManagement />
            </ProtectedDashboardRoute>
          }
        />

        {/* Reset password - without companySlug since email link doesn't include it (both hosts) */}
        <Route path="/owner/reset-password" element={
          <OwnerAuthProvider>
            <OwnerResetPassword />
          </OwnerAuthProvider>
        } />

        {isOwnerHost() ? (
          /* OWNER HOST (owner.hostea.io) — SHORT canonical paths: /:slug = login,
             /:slug/home|activity|statements|settings. Legacy /:slug/owner/* URLs
             (old emails, push payloads, app-host redirects) get their /owner
             segment stripped by the alias below. */
          <Route path="/:companySlug" element={<OwnerAuthProvider><Outlet /></OwnerAuthProvider>}>
            <Route index element={<OwnerLogin />} />
            <Route path="login" element={<OwnerLogin />} />
            <Route path="set-password" element={<OwnerSetPassword />} />
            <Route path="forgot-password" element={<OwnerForgotPassword />} />
            <Route element={
              <OwnerUnitProvider>
                <OwnerLayout />
              </OwnerUnitProvider>
            }>
              <Route path="home" element={<OwnerAccueil />} />
              <Route path="activity" element={<OwnerActivity />} />
              <Route path="statements" element={<OwnerReleves />} />
              <Route path="settings" element={<OwnerSettings />} />
            </Route>
            {/* Legacy long-form compat: strip the /owner segment */}
            <Route path="owner" element={<StripOwnerSegment />} />
            <Route path="owner/*" element={<StripOwnerSegment />} />
          </Route>
        ) : (
          /* APP HOST — legacy long paths (kept for dev/localhost; in prod the
             DomainGuard redirects these to the owner host before they render) */
          <>
            <Route path="/:companySlug/owner/login" element={
              <OwnerAuthProvider>
                <OwnerLogin />
              </OwnerAuthProvider>
            } />
            <Route path="/:companySlug/owner/set-password" element={
              <OwnerAuthProvider>
                <OwnerSetPassword />
              </OwnerAuthProvider>
            } />
            <Route path="/:companySlug/owner/forgot-password" element={
              <OwnerAuthProvider>
                <OwnerForgotPassword />
              </OwnerAuthProvider>
            } />
            <Route path="/:companySlug/owner" element={
              <OwnerAuthProvider>
                <OwnerUnitProvider>
                  <OwnerLayout />
                </OwnerUnitProvider>
              </OwnerAuthProvider>
            }>
              <Route index element={<Navigate to="home" replace />} />
              <Route path="home" element={<OwnerAccueil />} />
              <Route path="activity" element={<OwnerActivity />} />
              <Route path="statements" element={<OwnerReleves />} />
              <Route path="settings" element={<OwnerSettings />} />
            </Route>
          </>
        )}

        {/* Route pour le check-in - DOIT être avant les routes génériques */}
        <Route path="/checkin/:id" element={<GuestErrorBoundary><CheckInForm /></GuestErrorBoundary>} />
        {/* Route pour le check-in avec companyName */}
        <Route path="/:companyName/checkin/:id" element={<GuestErrorBoundary><CheckInForm /></GuestErrorBoundary>} />
        {/* Page d'achat early check-in autonome (cible du lien SMS/email d'outreach).
            Surface guest app : le chemin vit sous "/guestbook/" → après paiement,
            PaymentSuccess prend la branche guest app (confirmation + "Return to guest app"). */}
        <Route path="/guestbook/:id/early-checkin" element={<GuestErrorBoundary><Suspense fallback={<div />}><EarlyCheckinPurchase /></Suspense></GuestErrorBoundary>} />
        <Route path="/:companyName/guestbook/:id/early-checkin" element={<GuestErrorBoundary><Suspense fallback={<div />}><EarlyCheckinPurchase /></Suspense></GuestErrorBoundary>} />
        <Route path="/guestbook/:id/late-checkout" element={<GuestErrorBoundary><Suspense fallback={<div />}><LateCheckoutPurchase /></Suspense></GuestErrorBoundary>} />
        <Route path="/:companyName/guestbook/:id/late-checkout" element={<GuestErrorBoundary><Suspense fallback={<div />}><LateCheckoutPurchase /></Suspense></GuestErrorBoundary>} />
        <Route path="/guestbook/:id/extend-stay" element={<GuestErrorBoundary><Suspense fallback={<div />}><ExtendStayPurchase /></Suspense></GuestErrorBoundary>} />
        <Route path="/:companyName/guestbook/:id/extend-stay" element={<GuestErrorBoundary><Suspense fallback={<div />}><ExtendStayPurchase /></Suspense></GuestErrorBoundary>} />
        <Route path="/guestbook/:id/review" element={<GuestErrorBoundary><Suspense fallback={<div />}><GuestReview /></Suspense></GuestErrorBoundary>} />
        <Route path="/:companyName/guestbook/:id/review" element={<GuestErrorBoundary><Suspense fallback={<div />}><GuestReview /></Suspense></GuestErrorBoundary>} />

        {/* Routes pour le site de vente */}
        <Route path="/apartment/:apartmentId" element={<Information />} />
        {/* <Route path="/services/:apartmentId" element={<Services />} /> */}
        <Route path="/taxi/:apartmentId" element={<Taxi />} />
        <Route path="/chatbot/:apartmentId" element={<PreviewChatbot />} />
        <Route path="/payment-success" element={<PaymentSuccess themeColor="teal.500" />} />
        <Route path="/payment-cancel" element={<PaymentCancel themeColor="teal.500" />} />
        <Route path="/payment-pending" element={<PaymentPending themeColor="teal.500" />} />
        <Route path="/payment-redirect" element={<PaymentRedirect />} />

        {/* Route pour la confirmation de paiement */}
        <Route path="/payment-confirmation" element={<PaymentConfirmation />} />

        {/* Route pour la page de paiement */}
        <Route path="/payment/:reservationId/:serviceType" element={<PaymentPage />} />
        {/* Route pour la page de paiement depuis le check-in */}
        <Route path="/payment/checkin/:reservationId/:serviceType" element={<PaymentPage />} />

        {/* Page d'erreur */}
        <Route
          path="/error"
          element={<ErrorPage />}
        />
        {/* Accès refusé (cible des redirects backend authStaffMiddleware) */}
        <Route
          path="/unauthorized"
          element={
            <ErrorPageWrapper
              code={403}
              title="Accès non autorisé"
              message="Votre lien d'accès n'est plus valide ou a expiré. Veuillez utiliser le lien original qui vous a été envoyé."
            />
          }
        />

        {/* Route pour la redirection du guestbook */}
        <Route path="/guestbook-redirect/:slug" element={<GuestbookRedirect />} />
        {/* Route pour la redirection du guestbook avec companyName */}
        <Route path="/:companyName/guestbook-redirect/:unitId" element={<GuestbookRedirect />} />
        {/* Route pour le guestbook */}
        {/* Phone of the dashboard Guest app studio (fed by postMessage, same origin only) */}
        <Route path="/guestbook-preview" element={<Suspense fallback={<div />}><GuestAppPreviewFrame /></Suspense>} />
        <Route path="/checkin-preview" element={<Suspense fallback={<div />}><CheckinPreviewFrame /></Suspense>} />
        <Route path="/guestbook/:token" element={<GuestErrorBoundary><UnitWelcomeBook /></GuestErrorBoundary>} />
        {/* Route pour le guestbook avec companyName */}
        <Route path="/:companyName/guestbook/:token" element={<GuestErrorBoundary><UnitWelcomeBook /></GuestErrorBoundary>} />

        {/* Route pour les détails des services */}
        <Route path="/service/:hotelSlug/:serviceSlug" element={<ServiceDetails />} />

        {/* Interface mobile pour les utilisateurs */}
        <Route path="/staff/:email" element={<StaffMobileInterface />} />

        {/* Routes livret - dynamiques, doivent être avant le catch-all.
            Gated off the owner host: /:slug and /:slug/:x would collide with
            the short owner routes (/:slug = login, /:slug/home...). */}
        {!isOwnerHost() && (
          <>
            {/* Route pour une reservation spécifique - DOIT être avant /:slug */}
            <Route path="/:slug/:token" element={<GuestErrorBoundary><UnitWelcomeBook /></GuestErrorBoundary>} />
            {/* Route pour une unité spécifique */}
            <Route path="/:slug/:unitId" element={<GuestErrorBoundary><UnitWelcomeBook /></GuestErrorBoundary>} />
            {/* Route pour l'hôtel public - DOIT être avant le catch-all */}
            <Route path="/:slug" element={<GuestErrorBoundary><UnitWelcomeBook /></GuestErrorBoundary>} />
          </>
        )}

        {/* Catch-all 404 - DOIT être en DERNIER */}
        <Route path="*" element={<ErrorPageWrapper code={404} />} />
      </Routes>
    </Suspense>
  );
};

const AppRouter = () => {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <HotelProvider>
        <Suspense fallback={<LoadingFallback />}>
          <DomainGuard>
            <AppContent />
          </DomainGuard>
        </Suspense>
      </HotelProvider>
    </Router>
  );
};

export default AppRouter;
